import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, inject, OnChanges, SimpleChanges } from '@angular/core';
import * as echarts from 'echarts';
import { TreemapChartComponent as SharedTreemapChartComponent } from '@shared-lib/chart-components/treemap-chart/treemap-chart.component';
import { DataErrorComponent } from '@shared-lib/global-components/data-error/data-error.component';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { ConfigService } from '@shared-lib/shared-services/config.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';

@Component({
  selector: 'app-tree-map-chart',
  standalone: true,
  imports: [CommonModule, DataErrorComponent],
  templateUrl: './treemap-chart.component.html',
  styleUrl: './treemap-chart.component.scss',
})
export class TreemapChartComponent
  extends SharedTreemapChartComponent
  implements AfterViewInit, OnChanges {
  public displayTitle = '';

  constructor() {
    super(
      inject(ConfigService),
      inject(GlobalFilterService),
      inject(ChartFilterHandlerService),
    );
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['data'] || !this.myChart || this.data?.responseData === undefined) {
      return;
    }

    this.myChart.showLoading();
    this.handleResponse(this.data.responseData);
  }

  override ngAfterViewInit(): void {
    this.isFullscreenFocus = this.isInFullscreenFocus();

    if (this.data?.responseData !== undefined) {
      if (this.initChart()) {
        this.myChart.showLoading();
        this.handleResponse(this.data.responseData);
      }
      return;
    }

    if (this.data?.skipAutoLoad) {
      this.initChart();
      return;
    }

    super.ngAfterViewInit();
  }

  override extractTreeData(res: any): any[] {
    const flatRows = this.resolveFlatRows(res);
    if (flatRows.length > 0) {
      this.updateDynamicTitle(flatRows);
      return this.buildTreeFromFlatRows(flatRows);
    }

    this.displayTitle = this.data?.title || '';
    return super.extractTreeData(res);
  }

  override renderChart(treeData: any): void {
    const colors = this.data?.chartConfig?.colorMap || {};
    const fallbackColors = ['#1a6a85', '#ed6b2e', '#4b5563', '#8b5cf6'];

    this.allParentNames = treeData.map((item: any) => item.name);
    this.legendItems = [];

    treeData.forEach((parent: any, index: number) => {
      const color = colors[parent.name] || fallbackColors[index % fallbackColors.length];
      parent.itemStyle = { color };
      parent.children = (parent.children || []).map((child: any) => ({
        ...child,
        parentName: parent.name,
        itemStyle: {
          ...(child.itemStyle || {}),
          color,
        },
      }));
      this.legendItems.push({ name: parent.name, color });
    });

    const option: echarts.EChartsOption = {
      legend: { show: false },
      tooltip: {
        show: this.data?.chartConfig?.tooltip?.enabled !== false,
        trigger: 'item',
      },
      series: [
        {
          type: 'treemap',
          data: treeData,
          roam: false,
          nodeClick: false,
          breadcrumb: { show: false },
          left: 0,
          right: 0,
          top: 0,
          bottom: 0,
          itemStyle: {
            borderColor: '#ffffff',
            borderWidth: 2,
            gapWidth: 2,
          },
          upperLabel: { show: false },
          label: {
            show: true,
            position: 'insideTopLeft',
            padding: [8, 8, 8, 8],
            formatter: (params: any) => {
              const parentName = params.data?.parentName || params.treePathInfo?.[1]?.name || '';
              const childName = params.name || '';
              return parentName && parentName !== childName
                ? `{parent|${parentName}}\n{child|${childName}}`
                : `{parent|${childName}}`;
            },
            rich: {
              parent: {
                color: '#ffffff',
                fontSize: 13,
                fontWeight: 600,
                lineHeight: 17,
              },
              child: {
                color: '#ffffff',
                fontSize: 12,
                fontWeight: 400,
                lineHeight: 16,
              },
            },
          },
          levels: [
            {
              itemStyle: {
                borderColor: '#ffffff',
                borderWidth: 2,
                gapWidth: 2,
              },
            },
          ],
        },
      ],
    };

    this.myChart.setOption(option, true);
    this.myChart.resize();
    setTimeout(() => this.myChart?.hideLoading(), 200);
  }

  protected initChart(): boolean {
    const chartDom = document.getElementById(this.widgetId);
    if (!chartDom) return false;

    if (!this.myChart) {
      this.myChart = echarts.init(chartDom, 'dvm');
    }

    return true;
  }

  protected resolveFlatRows(res: any): any[] {
    const path = this.data?.flatRowsPath || this.data?.flatDataPath;
    if (!path) {
      return [];
    }

    const rows = this.resolvePath(res, path);
    return Array.isArray(rows) ? rows : [];
  }

  protected buildTreeFromFlatRows(rows: any[]): any[] {
    const parentKey = this.data?.flatParentKey || 'attribute';
    const childKey = this.data?.flatChildKey || 'sub_reason_won_lost';
    const childFallbackKey = this.data?.flatChildFallbackKey || 'reason_won_lost';
    const valueKey = this.data?.flatValueKey || 'value';
    const parentMap = new Map<string, { name: string; value: number; children: any[] }>();

    for (const row of rows) {
      const parentName = this.normalizeTreemapLabel(this.readConfiguredValue(row, parentKey), 'Other');
      const childName = this.normalizeTreemapLabel(
        this.readConfiguredValue(row, childKey) ?? this.readConfiguredValue(row, childFallbackKey),
        parentName,
      );
      const value = this.toFiniteNumber(this.readConfiguredValue(row, valueKey));

      if (!parentMap.has(parentName)) {
        parentMap.set(parentName, { name: parentName, value: 0, children: [] });
      }

      const parent = parentMap.get(parentName)!;
      parent.value += value;

      const existingChild = parent.children.find((child: any) => child.name === childName);
      if (existingChild) {
        existingChild.value += value;
      } else {
        parent.children.push({ name: childName, value });
      }
    }

    return this.applyConfiguredParentOrder(
      Array.from(parentMap.values()).filter((parent) => parent.children.length > 0),
    );
  }

  protected updateDynamicTitle(rows: any[]): void {
    const titleKey = this.data?.titleKey || this.data?.headingKey;
    if (titleKey) {
      const title = rows
        .map((row) => this.readConfiguredValue(row, titleKey))
        .find((value) => value !== undefined && value !== null && String(value).trim());

      this.displayTitle = title ? String(title).trim() : this.data?.title || '';
      return;
    }

    const template = this.data?.titleTemplate || this.data?.dynamicTitleTemplate;
    if (!template) {
      this.displayTitle = this.data?.title || '';
      return;
    }

    const parentKey = this.data?.flatParentKey || 'attribute';
    const valueKey = this.data?.flatValueKey || 'value';
    const winAttribute = String(this.data?.winAttribute || 'Closed Won').trim().toLowerCase();
    const decimals = Number(this.data?.winRateDecimals ?? 0);
    const total = rows.reduce((sum, row) => sum + this.toFiniteNumber(this.readConfiguredValue(row, valueKey)), 0);
    const won = rows
      .filter((row) => String(this.readConfiguredValue(row, parentKey) || '').trim().toLowerCase() === winAttribute)
      .reduce((sum, row) => sum + this.toFiniteNumber(this.readConfiguredValue(row, valueKey)), 0);
    const winRate = total > 0 ? (won / total) * 100 : 0;
    const formattedWinRate = this.formatNumber(winRate, decimals);

    this.displayTitle = String(template)
      .replace(/\{winRate\}/g, formattedWinRate)
      .replace(/\{won\}/g, this.formatNumber(won, 0))
      .replace(/\{total\}/g, this.formatNumber(total, 0));
  }

  protected applyConfiguredParentOrder(treeData: any[]): any[] {
    const order = Array.isArray(this.data?.parentOrder)
      ? this.data.parentOrder.map((item: any) => String(item || '').trim().toLowerCase())
      : [];

    if (!order.length) {
      return treeData;
    }

    return treeData
      .map((item, index) => ({ item, index }))
      .sort((left, right) => {
        const leftRank = order.indexOf(String(left.item?.name || '').trim().toLowerCase());
        const rightRank = order.indexOf(String(right.item?.name || '').trim().toLowerCase());
        const normalizedLeftRank = leftRank === -1 ? Number.MAX_SAFE_INTEGER : leftRank;
        const normalizedRightRank = rightRank === -1 ? Number.MAX_SAFE_INTEGER : rightRank;

        if (normalizedLeftRank !== normalizedRightRank) {
          return normalizedLeftRank - normalizedRightRank;
        }

        return left.index - right.index;
      })
      .map(({ item }) => item);
  }

  protected readConfiguredValue(row: any, key: string | string[] | undefined): any {
    if (!row || !key) {
      return undefined;
    }

    const keys = Array.isArray(key) ? key : [key];
    for (const currentKey of keys) {
      const value = currentKey.includes('.') ? this.resolvePath(row, currentKey) : row[currentKey];
      if (value !== undefined && value !== null && value !== '') {
        return value;
      }
    }

    return undefined;
  }

  protected normalizeTreemapLabel(value: any, fallback: string): string {
    if (value === undefined || value === null) {
      return fallback;
    }

    const label = String(value).trim();
    return label || fallback;
  }

  protected toFiniteNumber(value: any): number {
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : 0;
  }

  protected formatNumber(value: number, decimals: number): string {
    return value.toLocaleString(undefined, {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals,
    });
  }
}
